import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useSubscription } from '../contexts/SubscriptionContext';
import { 
  CreditCard, 
  CheckCircle, 
  Star,
  Lightning,
  ShieldCheck,
  Stethoscope,
  Pill,
  ClipboardText
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function PaymentModal({ open, onOpenChange, onPaymentComplete }) {
  const { subscription, refreshSubscription } = useSubscription();
  const [isLoading, setIsLoading] = useState(null); // 'single' or 'subscription'

  const handlePayment = async (packageId) => {
    setIsLoading(packageId);
    
    try {
      const { data } = await axios.post(
        `${API_URL}/api/payments/checkout`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );
      
      // Redirect to Stripe checkout
      window.location.href = data.url;
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Eroare la inițializarea plății. Vă rugăm încercați din nou.');
      setIsLoading(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="payment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CreditCard weight="duotone" className="w-6 h-6 text-primary" />
            Alege planul tău
          </DialogTitle>
          <DialogDescription>
            Selectează opțiunea potrivită pentru nevoile tale medicale
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Current status */}
          {subscription && (
            <div className="bg-muted rounded-lg p-3 text-sm">
              {subscription.is_subscribed ? (
                <p className="text-green-600 flex items-center gap-2">
                  <CheckCircle weight="fill" className="w-4 h-4" />
                  Abonament activ până la {new Date(subscription.expires_at).toLocaleDateString('ro-RO')}
                </p>
              ) : subscription.consultations_remaining > 0 ? (
                <p className="text-primary flex items-center gap-2">
                  <Lightning weight="fill" className="w-4 h-4" />
                  {subscription.consultations_remaining} consultație/consultații disponibilă(e)
                </p>
              ) : (
                <p className="text-muted-foreground">
                  Nu ai un abonament activ sau consultații disponibile
                </p>
              )}
            </div>
          )}

          {/* Single Consultation */}
          <div className="border border-border rounded-lg p-5 hover:border-primary/50 transition-colors">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-semibold text-lg text-foreground">Consultație Singulară</h3>
                <p className="text-sm text-muted-foreground">Plată unică</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-foreground">5€</span>
              </div>
            </div>
            
            <ul className="space-y-2 mb-4 text-sm">
              <li className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle weight="fill" className="w-4 h-4 text-green-500" />
                1 consultație AI completă
              </li>
              <li className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle weight="fill" className="w-4 h-4 text-green-500" />
                Analiză simptome și recomandări
              </li>
              <li className="flex items-center gap-2 text-muted-foreground">
                <CheckCircle weight="fill" className="w-4 h-4 text-green-500" />
                Încărcare documente medicale
              </li>
            </ul>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => handlePayment('single_consultation')}
              disabled={isLoading !== null}
              data-testid="buy-single-button"
            >
              {isLoading === 'single_consultation' ? (
                <>
                  <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin mr-2" />
                  Se procesează...
                </>
              ) : (
                <>
                  <CreditCard weight="bold" className="w-4 h-4 mr-2" />
                  Cumpără Consultație
                </>
              )}
            </Button>
          </div>

          {/* Monthly Subscription */}
          <div className="border-2 border-primary rounded-lg p-5 relative bg-primary/5">
            <div className="absolute -top-3 left-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
              <Star weight="fill" className="w-3 h-3" />
              RECOMANDAT
            </div>
            
            <div className="flex justify-between items-start mb-4 mt-2">
              <div>
                <h3 className="font-semibold text-lg text-foreground">Abonament Lunar</h3>
                <p className="text-sm text-muted-foreground">Acces complet</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-primary">10€</span>
                <span className="text-sm text-muted-foreground">/lună</span>
              </div>
            </div>
            
            <ul className="space-y-2 mb-4 text-sm">
              <li className="flex items-center gap-2 text-foreground">
                <CheckCircle weight="fill" className="w-4 h-4 text-green-500" />
                <strong>Consultații AI nelimitate</strong>
              </li>
              <li className="flex items-center gap-2 text-foreground">
                <ClipboardText weight="duotone" className="w-4 h-4 text-primary" />
                Istoric medical complet pentru consultații personalizate
              </li>
              <li className="flex items-center gap-2 text-foreground">
                <Pill weight="duotone" className="w-4 h-4 text-blue-500" />
                Acces la secțiunea Farmacie
              </li>
              <li className="flex items-center gap-2 text-foreground">
                <Stethoscope weight="duotone" className="w-4 h-4 text-purple-500" />
                Acces la Telemedicină
              </li>
              <li className="flex items-center gap-2 text-foreground">
                <ShieldCheck weight="duotone" className="w-4 h-4 text-green-500" />
                Prioritate la suport
              </li>
            </ul>

            <Button
              className="w-full"
              onClick={() => handlePayment('monthly_subscription')}
              disabled={isLoading !== null}
              data-testid="buy-subscription-button"
            >
              {isLoading === 'monthly_subscription' ? (
                <>
                  <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                  Se procesează...
                </>
              ) : (
                <>
                  <Star weight="fill" className="w-4 h-4 mr-2" />
                  Începe Abonamentul
                </>
              )}
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          Plățile sunt procesate securizat prin Stripe. 
          Prețurile sunt în EUR și pot varia în funcție de cursul valutar.
        </p>
      </DialogContent>
    </Dialog>
  );
}
